import { useState, useCallback } from 'react'
import { logger } from '@/utils/logger'

interface ChunkUploadOptions {
  file: File
  onProgress?: (progress: number) => void
  onComplete?: (result: any) => void
  onError?: (error: Error) => void
  chunkSize?: number
  uploadUrl?: string
  patient?: string
  slideBarcode?: string
  specimenType?: string
  anatomicSite?: string
}

interface ChunkInfo {
  index: number
  start: number
  end: number
  blob: Blob
  uploaded: boolean
}

export class ChunkedUploader {
  private file: File
  private chunkSize: number
  private uploadUrl: string
  private chunks: ChunkInfo[] = []
  private uploadedChunks = 0
  private totalChunks = 0
  private onProgress?: (progress: number) => void
  private onComplete?: (result: any) => void
  private onError?: (error: Error) => void
  private uploadId: string = ''
  private patient?: string
  private slideBarcode?: string
  private specimenType?: string
  private anatomicSite?: string

  constructor(options: ChunkUploadOptions) {
    this.file = options.file
    // Smaller chunks to stay under Cloudflare 100MB limit: 5MB chunks
    // Even with large files, each chunk stays well under the limit
    this.chunkSize = options.chunkSize || 5 * 1024 * 1024 // 5MB chunks
    this.uploadUrl = options.uploadUrl || '/api/v1/imaging/upload/chunked/'
    this.onProgress = options.onProgress
    this.onComplete = options.onComplete
    this.onError = options.onError
    this.patient = options.patient
    this.slideBarcode = options.slideBarcode
    this.specimenType = options.specimenType
    this.anatomicSite = options.anatomicSite
    this.uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private createChunks(): ChunkInfo[] {
    const chunks: ChunkInfo[] = []
    const fileSize = this.file.size
    let start = 0
    let index = 0

    while (start < fileSize) {
      const end = Math.min(start + this.chunkSize, fileSize)
      const blob = this.file.slice(start, end)

      chunks.push({
        index,
        start,
        end,
        blob,
        uploaded: false
      })

      start = end
      index++
    }

    return chunks
  }

  private async initializeUpload(): Promise<boolean> {
    try {
      const body: any = {
        upload_id: this.uploadId,
        filename: this.file.name,
        file_size: this.file.size,
        total_chunks: this.totalChunks,
        chunk_size: this.chunkSize,
        file_type: this.file.type,
        slide_id: 'SL-' + Date.now(),
        image_type: 'WSI'
      }

      // Patient-centric workflow
      if (this.patient) {
        body.patient = this.patient
      }

      // Specimen metadata
      if (this.specimenType) {
        body.specimen_type = this.specimenType
      }
      if (this.anatomicSite) {
        body.anatomic_site = this.anatomicSite
      }

      // Slide barcode (optional)
      if (this.slideBarcode) {
        body.slide_barcode = this.slideBarcode
      }

      const response = await fetch(`${this.uploadUrl}init/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        throw new Error(`Failed to initialize upload: ${response.statusText}`)
      }

      const result = await response.json()
      logger.debug('✅ Upload initialized:', result)
      return true
    } catch (error) {
      console.error('❌ Failed to initialize upload:', error)
      if (this.onError) {
        this.onError(error as Error)
      }
      return false
    }
  }

  private async uploadChunk(chunk: ChunkInfo, retryCount = 0): Promise<boolean> {
    const maxRetries = 3

    try {
      const formData = new FormData()
      formData.append('upload_id', this.uploadId)
      formData.append('chunk_index', chunk.index.toString())
      formData.append('chunk_start', chunk.start.toString())
      formData.append('chunk_end', chunk.end.toString())
      formData.append('chunk', chunk.blob)

      const response = await fetch(`${this.uploadUrl}chunk/`, {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Failed to upload chunk ${chunk.index}: ${response.statusText}`)
      }

      const result = await response.json()

      if (result.success) {
        chunk.uploaded = true
        this.uploadedChunks++

        // Only show up to 95% progress for chunks, reserve 95-100% for finalization
        const chunkProgress = Math.round((this.uploadedChunks / this.totalChunks) * 95)
        logger.debug(`📦 Chunk ${chunk.index + 1}/${this.totalChunks} uploaded (${chunkProgress}%)`)

        if (this.onProgress) {
          this.onProgress(chunkProgress)
        }

        return true
      } else {
        throw new Error(result.error || 'Chunk upload failed')
      }
    } catch (error) {
      console.error(`❌ Failed to upload chunk ${chunk.index}:`, error)

      // Retry failed chunk
      if (retryCount < maxRetries) {
        logger.debug(`🔄 Retrying chunk ${chunk.index} (attempt ${retryCount + 1}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))) // Exponential backoff
        return this.uploadChunk(chunk, retryCount + 1)
      }

      return false
    }
  }

  private async finalizeUpload(retryCount = 0): Promise<any> {
    const maxRetries = 3

    try {
      // Update progress to 96% when starting finalization
      if (this.onProgress) {
        this.onProgress(96)
      }

      const response = await fetch(`${this.uploadUrl}finalize/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          upload_id: this.uploadId,
          retry_finalize: retryCount > 0
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to finalize upload: ${response.statusText} - ${errorText}`)
      }

      const result = await response.json()

      if (!result.success) {
        // Check if it's a missing chunks error that we can retry
        if (result.can_retry && result.missing_chunks && retryCount < maxRetries) {
          logger.debug(`⚠️ Missing chunks detected: ${result.missing_chunks.length}/${result.total_chunks} missing`)
          logger.debug(`📋 Missing chunks: ${result.missing_chunks.slice(0, 5).join(', ')}${result.missing_chunks.length > 5 ? '...' : ''}`)
          logger.debug(`🔄 Retrying finalization (attempt ${retryCount + 1}/${maxRetries})`)

          // Wait longer for chunks to arrive
          await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)))

          // Update progress to show retry
          if (this.onProgress) {
            this.onProgress(97 + retryCount)
          }

          return this.finalizeUpload(retryCount + 1)
        } else {
          throw new Error(result.error || 'Finalization failed')
        }
      }

      logger.debug('✅ Upload finalized:', result)
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (retryCount < maxRetries && errorMessage.includes('Missing chunks')) {
        logger.debug(`⚠️ Finalization failed, retrying (attempt ${retryCount + 1}/${maxRetries}):`, errorMessage)

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)))

        return this.finalizeUpload(retryCount + 1)
      }

      console.error('❌ Failed to finalize upload after retries:', error)
      throw error
    }
  }

  async upload(): Promise<any> {
    try {
      logger.debug(`🚀 Starting chunked upload: ${this.file.name} (${(this.file.size / 1024 / 1024).toFixed(2)} MB)`)

      // Create chunks
      this.chunks = this.createChunks()
      this.totalChunks = this.chunks.length

      logger.debug(`📦 Created ${this.totalChunks} chunks of ${(this.chunkSize / 1024 / 1024).toFixed(1)}MB each`)

      // Initialize upload
      const initialized = await this.initializeUpload()
      if (!initialized) {
        throw new Error('Failed to initialize upload')
      }

      // Upload chunks in parallel (max 3 concurrent to avoid database connection overload)
      const maxConcurrent = 3
      const chunksToUpload = [...this.chunks]

      while (chunksToUpload.length > 0) {
        const batch = chunksToUpload.splice(0, maxConcurrent)
        const promises = batch.map(chunk => this.uploadChunk(chunk))
        const results = await Promise.all(promises)

        // Check if any chunk failed
        const failed = results.some(result => !result)
        if (failed) {
          throw new Error('Some chunks failed to upload')
        }
      }

      // Finalize upload
      const result = await this.finalizeUpload()

      // Only set progress to 100% after successful finalization
      if (this.onProgress) {
        this.onProgress(100)
      }

      if (this.onComplete) {
        this.onComplete(result)
      }

      return result
    } catch (error) {
      console.error('❌ Chunked upload failed:', error)
      if (this.onError) {
        this.onError(error as Error)
      }
      throw error
    }
  }

  cancel(): void {
    // TODO: Implement cancel functionality
    logger.debug('🛑 Upload cancelled')
  }
}

// React Hook for chunked upload
export const useChunkedUpload = () => {
  const [progress, setProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadFile = useCallback(async (
    file: File,
    options?: Partial<ChunkUploadOptions>
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      setIsUploading(true)
      setProgress(0)
      setError(null)

      const uploader = new ChunkedUploader({
        file,
        onProgress: (progress) => {
          setProgress(progress)
        },
        onComplete: (result) => {
          setIsUploading(false)
          setProgress(100)
          resolve(result)
        },
        onError: (error) => {
          setIsUploading(false)
          setError(error.message)
          reject(error)
        },
        ...options
      })

      uploader.upload()
    })
  }, [])

  return {
    uploadFile,
    progress,
    isUploading,
    error
  }
}